/**
 * @fileoverview NotesFeedView — presentational orchestrator for a
 * chronological contact notes feed.
 *
 * Scope-agnostic: shared by the job-contact and firm-contact feeds. It
 * owns three regions and all the composer UX (draft state, Cmd/Ctrl+Enter,
 * posting spinner), but knows nothing about Convex — persistence and
 * summarisation are delegated to the callbacks the container supplies:
 *
 * 1. **Header** — Title, count pill, "Summarise with AI" + optional
 *    "Chat" buttons.
 * 2. **Composer** — `NotesEditor` with an explicit "Post note" submit
 *    (not autosave) so blurring the editor doesn't accidentally create
 *    a feed entry. Cmd / Ctrl + Enter posts.
 * 3. **Summary card + feed** — newest-first list of `NoteRow`s with
 *    inline edit + delete (author-only).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Id } from "@cvx/_generated/dataModel";
import { Loader2, MessageSquare, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  NotesEditor,
  type NotesEditorHandle,
} from "@/ui/composed/editor/notes-editor";
import { NoteRow, type FeedNote } from "./NoteRow";
import { NotesSummaryCard } from "./NotesSummaryCard";

interface FeedSummary {
  text: string;
  generatedAt: number;
  noteCount: number;
}

interface NotesFeedViewProps {
  notes: Array<FeedNote>;
  /** Live notes count for the header badge + summary stale-detection. */
  notesCount: number;
  isLoading: boolean;
  /** Read-only when the parent contact is merged / archived. */
  readOnly?: boolean;
  /** The current authenticated user (decides which rows are editable). */
  currentUserId?: Id<"users">;
  /** Cached AI summary from the parent. */
  summary?: FeedSummary | null;
  /** True while the parent is invoking the summarise action. */
  isSummarising?: boolean;
  /**
   * Show the AI "Summarise with AI" button + summary card. Defaults to true;
   * surfaces that don't wire a summarise action (e.g. tasks) pass `false`.
   */
  showSummary?: boolean;
  /** Show the "Chat" header button (job contacts only). */
  showChat?: boolean;
  onOpenChat?: () => void;
  /**
   * Composer prompt. Defaults to the contact-oriented copy; surfaces with a
   * narrower subject (a claim, a task) pass their own.
   */
  placeholder?: string;
  /** Persist a new note. Rejects on failure so the composer can toast. */
  onPost: (content: string) => Promise<void>;
  /** Persist an edit to an existing note. */
  onSaveNote: (noteId: string, content: string) => Promise<void>;
  /** Soft-delete an existing note. */
  onDeleteNote: (noteId: string) => Promise<void>;
  /** Generate the summary (no-op when there are no notes). */
  onGenerateSummary?: () => void;
  /** Force-refresh the summary. */
  onRefreshSummary?: () => void;
}

function isHtmlEmpty(html: string): boolean {
  const stripped = html
    .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return stripped.length === 0;
}

export function NotesFeedView({
  notes,
  notesCount,
  isLoading,
  readOnly = false,
  currentUserId,
  summary,
  isSummarising = false,
  showSummary = true,
  showChat = false,
  onOpenChat,
  placeholder = "Add a note about this contact — what was discussed, what to follow up on, anything worth remembering.",
  onPost,
  onSaveNote,
  onDeleteNote,
  onGenerateSummary,
  onRefreshSummary,
}: NotesFeedViewProps) {
  const [composerDraft, setComposerDraft] = useState<string>("");
  const [isPosting, setIsPosting] = useState(false);
  const composerRef = useRef<NotesEditorHandle | null>(null);

  const handlePost = useCallback(async () => {
    const content = composerDraft;
    if (isHtmlEmpty(content)) {
      toast.info("Write something before posting.");
      return;
    }
    setIsPosting(true);
    try {
      await onPost(content);
      setComposerDraft("");
      composerRef.current?.getEditor()?.commands.clearContent();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not post your note";
      toast.error(message);
    } finally {
      setIsPosting(false);
    }
  }, [composerDraft, onPost]);

  // Cmd / Ctrl + Enter posts the composer. Re-attached when handlePost
  // identity changes so the listener captures the latest draft.
  useEffect(() => {
    const editor = composerRef.current?.getEditor();
    if (!editor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void handlePost();
      }
    };
    const dom = editor.view.dom;
    dom.addEventListener("keydown", onKeyDown);
    return () => dom.removeEventListener("keydown", onKeyDown);
  }, [handlePost]);

  const composerIsEmpty = isHtmlEmpty(composerDraft);

  return (
    <section className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
            Notes
          </h3>
          <Badge variant="secondary" size="sm">
            {notesCount}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          {showSummary && (
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerateSummary}
              disabled={isSummarising || notesCount === 0}
              className="gap-1.5"
              title={
                notesCount === 0
                  ? "Post a note first"
                  : "Summarise the notes feed with AI"
              }
            >
              {isSummarising ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Summarise with AI
            </Button>
          )}
          {showChat && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenChat}
              className="gap-1.5"
              title="Chat with Morgan about this contact"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Chat
            </Button>
          )}
        </div>
      </div>

      {/* Composer */}
      {!readOnly && (
        <div className="flex flex-col gap-2">
          <NotesEditor
            ref={composerRef}
            value={composerDraft}
            onChange={setComposerDraft}
            placeholder={placeholder}
            minHeightClass="min-h-[100px]"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              Cmd/Ctrl + Enter to add
            </span>
            <Button
              size="sm"
              onClick={() => void handlePost()}
              disabled={isPosting || composerIsEmpty}
              className="gap-1.5"
            >
              {isPosting ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
              Add note
            </Button>
          </div>
        </div>
      )}

      {/* Summary card */}
      {showSummary && (
        <NotesSummaryCard
          summary={summary ?? null}
          liveNoteCount={notesCount}
          isGenerating={isSummarising}
          onRefresh={onRefreshSummary ?? (() => {})}
        />
      )}

      {/* Feed */}
      <div className="flex flex-col gap-2">
        {isLoading ? (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-border bg-muted/10 py-6 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading notes…
          </div>
        ) : notes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/10 p-6 text-center text-sm text-muted-foreground">
            No notes added yet. Add your first note above.
          </div>
        ) : (
          notes.map((n) => (
            <NoteRow
              key={n._id}
              note={n}
              author={
                n.authorAvatarUrl
                  ? { _id: n.createdBy, avatarUrl: n.authorAvatarUrl }
                  : undefined
              }
              canEdit={!!currentUserId && n.createdBy === currentUserId}
              readOnly={readOnly}
              onSave={(content) => onSaveNote(n._id, content)}
              onDelete={() => onDeleteNote(n._id)}
            />
          ))
        )}
      </div>
    </section>
  );
}
